/**
 * Deterministic debate orchestrator.
 *
 * NOT an LLM agent — a state machine. Owns the 5-phase turn order so the
 * model only has to do one job at a time: generate one utterance in one
 * voice. Phases:
 *
 *   1. opening           — each side states their position
 *   2. rebuttal          — each side answers the other's opening
 *   3. cross-ex          — alternating back-and-forth (the heat)
 *   4. closing           — each side's last word
 *   5. moderator-summary — moderator names sub-point winners and closes
 *
 * The plan is a flat sequence of (speakerId, phase) slots. The viewer
 * consumes them in order. The orchestrator never calls an LLM itself.
 */

import type { DebateFormat, DebatePhase } from '@arena/shared';

export const MODERATOR_ID = 'moderator';

export interface PlannedTurn {
  speakerId: string;
  phase: DebatePhase;
  /** 0-indexed slot number within the whole debate. */
  index: number;
}

export const DEFAULT_FORMAT: DebateFormat = {
  openingTurnsPerSide: 1,
  rebuttalTurnsPerSide: 1,
  crossExTurnsPerSide: 2,
  closingTurnsPerSide: 1,
  maxTokensPerTurn: 600,
};

/**
 * Produce the full turn plan for a two-sided debate.
 *
 * `left` always speaks first in opening; the rebuttal flips so `right`
 * answers `left`'s opening first; cross-ex alternates starting with `left`
 * again; closing flips so the side that opened gets the last word.
 */
export function planDebate(
  leftId: string,
  rightId: string,
  format: DebateFormat = DEFAULT_FORMAT,
): PlannedTurn[] {
  const turns: Array<Omit<PlannedTurn, 'index'>> = [];

  // Moderator opens with the topic + ground rules.
  turns.push({ speakerId: MODERATOR_ID, phase: 'opening' });

  // Opening statements: left, right.
  for (let i = 0; i < format.openingTurnsPerSide; i++) {
    turns.push({ speakerId: leftId, phase: 'opening' });
    turns.push({ speakerId: rightId, phase: 'opening' });
  }

  // Rebuttal: right answers first (since left opened), then left.
  for (let i = 0; i < format.rebuttalTurnsPerSide; i++) {
    turns.push({ speakerId: rightId, phase: 'rebuttal' });
    turns.push({ speakerId: leftId, phase: 'rebuttal' });
  }

  // Cross-examination: alternating, starting with left.
  for (let i = 0; i < format.crossExTurnsPerSide; i++) {
    turns.push({ speakerId: leftId, phase: 'cross-ex' });
    turns.push({ speakerId: rightId, phase: 'cross-ex' });
  }

  // Closing: right then left, so left (the opener) gets the last word.
  for (let i = 0; i < format.closingTurnsPerSide; i++) {
    turns.push({ speakerId: rightId, phase: 'closing' });
    turns.push({ speakerId: leftId, phase: 'closing' });
  }

  // Moderator names sub-point winners and closes.
  turns.push({ speakerId: MODERATOR_ID, phase: 'moderator-summary' });

  return turns.map((t, index) => ({ ...t, index }));
}

/**
 * Human-readable phase label for the transcript header.
 */
export function labelForPhase(phase: DebatePhase): string {
  switch (phase) {
    case 'opening':
      return 'Opening Statements';
    case 'rebuttal':
      return 'Rebuttal';
    case 'cross-ex':
      return 'Cross-Examination';
    case 'closing':
      return 'Closing Statements';
    case 'moderator-summary':
      return 'The Moderator';
  }
}
